import { useCallback, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { Difficulty } from '../types';

const SCORE_THRESHOLD_HIGH = 0.8;
const SCORE_THRESHOLD_LOW = 0.4;
const EWMA_ALPHA = 0.2; // Exponential Weighted Moving Average factor

export const useGameEngine = () => {
  const { state, dispatch } = useGame();

  // Initialize grid for a new level
  const initializeGrid = useCallback((difficulty: Difficulty) => {
    const { gridSize, obstaclesCount } = difficulty;
    const totalCells = gridSize * gridSize;
    const numbers = Array.from({ length: totalCells - obstaclesCount }, (_, i) => i + 1);
    
    // Create empty grid
    const grid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(0));
    const obstacles = Array(gridSize).fill(null).map(() => Array(gridSize).fill(false));
    
    // Place obstacles randomly
    let remainingObstacles = obstaclesCount;
    while (remainingObstacles > 0) {
      const row = Math.floor(Math.random() * gridSize);
      const col = Math.floor(Math.random() * gridSize);
      if (!obstacles[row][col]) {
        obstacles[row][col] = true;
        remainingObstacles--;
      }
    }
    
    // Place numbers randomly in non-obstacle cells
    let numbersIndex = 0;
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        if (!obstacles[i][j]) {
          grid[i][j] = numbers[numbersIndex++];
        }
      }
    }
    
    return { grid, obstacles };
  }, []);

  // Check if a move is valid
  const isValidMove = useCallback((row: number, col: number): boolean => {
    const { grid, obstacles, currentNumber, selectedCells } = state;
    
    // Check if cell is an obstacle or already selected
    if (obstacles[row][col] || selectedCells.some(([r, c]) => r === row && c === col)) {
      return false;
    }
    
    // Check if this is the next number in sequence
    if (grid[row][col] !== currentNumber) {
      return false;
    }
    
    // If this is the first move, it's valid
    if (selectedCells.length === 0) {
      return true;
    }
    
    // Check if the move is adjacent to the last selected cell
    const [lastRow, lastCol] = selectedCells[selectedCells.length - 1];
    const isAdjacent = Math.abs(row - lastRow) + Math.abs(col - lastCol) === 1;
    
    return isAdjacent;
  }, [state]);

  // Calculate score for the level
  const calculateScore = useCallback((): number => {
    const { timeElapsed, errors, difficulty } = state;
    const maxTime = difficulty.gridSize * difficulty.gridSize * 2; // 2 seconds per cell as baseline
    const timeScore = Math.max(0, 1 - timeElapsed / maxTime);
    const accuracyScore = Math.max(0, 1 - errors / (difficulty.gridSize * difficulty.gridSize));
    
    return (timeScore * 0.4 + accuracyScore * 0.6); // Weight accuracy more than time
  }, [state]);

  // Adjust difficulty based on performance
  const adjustDifficulty = useCallback((score: number): Difficulty => {
    const { difficulty } = state;
    let { gridSize, obstaclesCount, showHints } = difficulty;
    
    if (score >= SCORE_THRESHOLD_HIGH) {
      gridSize = Math.min(8, gridSize + 1);
      obstaclesCount = Math.min(Math.floor(gridSize * gridSize * 0.3), obstaclesCount + 1);
      showHints = false;
    } else if (score <= SCORE_THRESHOLD_LOW) {
      gridSize = Math.max(3, gridSize - 1);
      obstaclesCount = Math.max(1, obstaclesCount - 1);
      showHints = true;
    }
    
    return { gridSize, obstaclesCount, showHints };
  }, [state]);

  // Handle cell selection
  const handleCellClick = useCallback((row: number, col: number) => {
    if (state.isPaused || state.isComplete) return;
    
    if (isValidMove(row, col)) {
      dispatch({ type: 'SELECT_CELL', payload: { row, col } });
    } else {
      dispatch({ type: 'INCREMENT_ERROR' });
    }
  }, [state, isValidMove, dispatch]);

  // Handle keyboard navigation
  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (state.isPaused || state.isComplete) return;
    
    const [lastRow, lastCol] = state.selectedCells[state.selectedCells.length - 1] || [0, 0];
    let newRow = lastRow;
    let newCol = lastCol;
    
    switch (e.key) {
      case 'ArrowUp':
        newRow = Math.max(0, lastRow - 1);
        break;
      case 'ArrowDown':
        newRow = Math.min(state.difficulty.gridSize - 1, lastRow + 1);
        break;
      case 'ArrowLeft':
        newCol = Math.max(0, lastCol - 1);
        break;
      case 'ArrowRight':
        newCol = Math.min(state.difficulty.gridSize - 1, lastCol + 1);
        break;
      case 'Enter':
        if (isValidMove(newRow, newCol)) {
          handleCellClick(newRow, newCol);
        }
        break;
      default:
        return;
    }
    
    e.preventDefault();
  }, [state, isValidMove, handleCellClick]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  return {
    initializeGrid,
    handleCellClick,
    isValidMove,
    calculateScore,
    adjustDifficulty
  };
};